import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { action, computed, makeObservable, observable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';

import {
  planDisplayTextMap,
  SubscriptionPlan,
  type SubscriptionState,
} from './subscription.types';

export class SubscriptionStore {
  state: SubscriptionState = {
    currentPlan: SubscriptionPlan.BASIC,
    mcpServerPermitted: false,
    currentPlanDisplayName: null,
    planChangeDate: null,
    stripeCustomerId: null,
    cancelAtPeriodEnd: false,
    subscriptionStatus: undefined,
    currentPeriodEnd: undefined,
    subscriptionId: undefined,
    trialDaysRemaining: undefined,
    plusPromoOffer: null,
    powerUpgradeOffer: null,
    paymentMethod: undefined,
    endedInTrial: false,
    meeting_limits: undefined,
    researchLimits: undefined,
    journalLimits: undefined,
    routinesLimits: undefined,
    hasAppliedReferral: undefined,
    isFree: false,
    referralStatus: undefined,
    referralRewardType: null,
    userReferralRewardType: null,
    accountCreditCents: undefined,
    creditBalance: 0,
    creditTotal: 0,
    teamPoolAvailable: 0,
    lowBalanceThreshold: 0,
    liteModeActive: false,
    creditLimits: undefined,
    autoRefill: { enabled: false },
    isLoading: true,
    error: null,
    lastUpdated: null,
    pricingPlans: [],
    pricingPlansLoading: false,
    pricingPlansError: null,
    team: null,
    isTeamMember: false,
  };

  checkoutLoading: boolean = false;
  cancellingLoading: boolean = false;
  resumingLoading: boolean = false;
  portalLoading: boolean = false;
  upgradeLoading: boolean = false;

  constructor() {
    makeObservable(this, {
      state: observable,
      checkoutLoading: observable,
      cancellingLoading: observable,
      resumingLoading: observable,
      portalLoading: observable,
      upgradeLoading: observable,
      // computed values
      isActive: computed,
      isPlus: computed,
      isPro: computed,
      isGodAvailable: computed,
      isArgonAvailable: computed,
      teamAllowedChatModes: computed,
      isPowerOrPro: computed,
      hasMcpServerAccess: computed,
      isNoCardTrial: computed,
      currentPlan: computed,
      currentPlanLabel: computed,
      currentPlanDetails: computed,
      billingInterval: computed,
      nextBillingDate: computed,
      cancelAtPeriodEnd: computed,
      hasReachedMeetingLimit: computed,
      isResearchLimitHit: computed,
      isOutOfCredits: computed,
      isLowOnCredits: computed,
      isLiteModeActive: computed,
      canUpgrade: computed,
      isFree: computed,
      isAppStoreSubscriber: computed,
      isTeamMember: computed,
      isTeamAdmin: computed,
      // actions
      updateState: action,
      setCheckoutLoading: action,
      setCancellingLoading: action,
      setResumingLoading: action,
      setPortalLoading: action,
      setUpgradeLoading: action,
    });

    makePersistable(this, {
      name: 'subscription',
      properties: ['state'],
      storage: window.localStorage,
    });
  }

  get isActive(): boolean {
    return (
      this.state.currentPlan === SubscriptionPlan.PLUS_MONTHLY ||
      this.state.currentPlan === SubscriptionPlan.PLUS_ANNUAL ||
      this.state.currentPlan === SubscriptionPlan.POWER_MONTHLY ||
      this.state.currentPlan === SubscriptionPlan.POWER_ANNUAL ||
      this.state.currentPlan === SubscriptionPlan.PRO_MONTHLY ||
      this.state.currentPlan === SubscriptionPlan.PRO2_MONTHLY
    );
  }

  get isPlus(): boolean {
    return (
      this.state.currentPlan === SubscriptionPlan.PLUS_MONTHLY ||
      this.state.currentPlan === SubscriptionPlan.PLUS_ANNUAL ||
      this.state.currentPlan === SubscriptionPlan.POWER_MONTHLY ||
      this.state.currentPlan === SubscriptionPlan.POWER_ANNUAL ||
      this.state.currentPlan === SubscriptionPlan.PRO_MONTHLY ||
      this.state.currentPlan === SubscriptionPlan.PRO2_MONTHLY
    );
  }

  get isNoCardTrial(): boolean {
    return (
      this.state.subscriptionStatus === 'trialing' && !this.state.paymentMethod
    );
  }

  get noCardTrialDaysLeft(): number | null {
    const end = this.state.currentPeriodEnd;
    if (!this.isNoCardTrial || !end) return null;
    return Math.max(
      0,
      differenceInCalendarDays(new Date(end * 1000), new Date())
    );
  }

  get hasMcpServerAccess(): boolean {
    return this.state.mcpServerPermitted === true;
  }

  /** Pro tier — unlocks Ultra chat mode. */
  get isPro(): boolean {
    return (
      this.state.currentPlan === SubscriptionPlan.PRO_MONTHLY ||
      this.state.currentPlan === SubscriptionPlan.PRO2_MONTHLY
    );
  }

  get isGodAvailable(): boolean {
    return this.state.routinesLimits?.god_enabled === true;
  }

  get isArgonAvailable(): boolean {
    return this.state.routinesLimits?.argon_enabled === true;
  }

  get teamAllowedChatModes(): string[] | null | undefined {
    return this.state.team?.allowed_chat_modes;
  }

  /** Power or better — the tier that unlocks Power/Pro-only features. */
  get isPowerOrPro(): boolean {
    return (
      this.state.currentPlan === SubscriptionPlan.POWER_MONTHLY ||
      this.state.currentPlan === SubscriptionPlan.POWER_ANNUAL ||
      this.state.currentPlan === SubscriptionPlan.PRO_MONTHLY ||
      this.state.currentPlan === SubscriptionPlan.PRO2_MONTHLY
    );
  }

  get currentPlan(): SubscriptionPlan {
    return this.state.currentPlan;
  }

  get currentPlanLabel(): string {
    return (
      this.state.currentPlanDisplayName ??
      planDisplayTextMap[this.state.currentPlan]
    );
  }

  get currentPlanDetails() {
    return (
      this.state.pricingPlans.find((p) => p.id === this.state.currentPlan) ||
      null
    );
  }

  get billingInterval(): 'Annual' | 'Monthly' {
    return this.state.currentPlan.endsWith('_annual') ? 'Annual' : 'Monthly';
  }

  get nextBillingDate(): Date | null {
    if (!this.state.planChangeDate || !this.isActive) return null;

    const next = new Date(this.state.planChangeDate);
    if (this.billingInterval === 'Annual') {
      next.setFullYear(next.getFullYear() + 1);
    } else {
      next.setMonth(next.getMonth() + 1);
    }

    return next;
  }

  get cancelAtPeriodEnd(): boolean {
    return this.state.cancelAtPeriodEnd;
  }

  get hasReachedMeetingLimit(): boolean {
    return this.state.meeting_limits?.is_limit_hit ?? false;
  }

  get isResearchLimitHit(): boolean {
    return this.state.researchLimits?.is_limit_hit ?? false;
  }

  private get availableCredits(): number {
    return this.state.creditTotal + this.state.teamPoolAvailable;
  }

  get isOutOfCredits(): boolean {
    return this.state.lastUpdated !== null && this.availableCredits <= 0;
  }

  get isLiteModeActive(): boolean {
    return this.state.liteModeActive;
  }

  get isLowOnCredits(): boolean {
    return (
      this.state.lastUpdated !== null &&
      this.state.lowBalanceThreshold > 0 &&
      this.availableCredits > 0 &&
      this.availableCredits <= this.state.lowBalanceThreshold
    );
  }

  get canUpgrade(): boolean {
    return this.state.currentPlan !== SubscriptionPlan.PRO2_MONTHLY;
  }

  get isFree(): boolean {
    return this.state.isFree;
  }

  get isTeamMember(): boolean {
    return this.state.isTeamMember;
  }

  get isTeamAdmin(): boolean {
    const role = this.state.team?.role;
    return this.isTeamMember && (role === 'owner' || role === 'admin');
  }

  get isAppStoreSubscriber(): boolean {
    const hasActiveStripeSubscription = [
      'active',
      'trialing',
      'past_due',
    ].includes(this.state.subscriptionStatus ?? '');
    return this.isActive && !hasActiveStripeSubscription;
  }

  updateState(newState: Partial<SubscriptionState>): void {
    this.state = { ...this.state, ...newState };
  }

  setCheckoutLoading(loading: boolean): void {
    this.checkoutLoading = loading;
  }

  setCancellingLoading(loading: boolean): void {
    this.cancellingLoading = loading;
  }

  setResumingLoading(loading: boolean): void {
    this.resumingLoading = loading;
  }

  setPortalLoading(loading: boolean): void {
    this.portalLoading = loading;
  }

  setUpgradeLoading(loading: boolean): void {
    this.upgradeLoading = loading;
  }
}

export const subscriptionStore = new SubscriptionStore();
